import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { BookOpen, CalendarRange, Eye, GraduationCap, IndianRupee, Search, TrendingUp, Wallet } from "lucide-react";
import { getTransactionForecast, getUserTransactionDetails } from "../../Action/courseAction";
import {
  CustomButton,
  CustomDatePicker,
  CustomIconButton,
  CustomPageLayout,
  CustomStatCard,
} from "../../Component/customComponents";
import { TotalsModal, formatINR, formatDateRange } from "../Common/transactionCommon";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const TYPES = { edutech: "Edutech", publication: "Publication" };
const EMPTY_FORECAST = { avgSales: "0", saleForecast: "0" };

// last 30 days, read when the page opens (as before)
const defaultRange = () => ({
  startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
  endDate: moment().format("YYYY-MM-DD"),
});

/** "21-10-2026" (the API's _id) → moment */
const parseDay = (id) => moment(id, "DD-MM-YYYY");

/** Sums for the cards (same as before) */
const computeTotals = (days) =>
  days.reduce(
    (t, d) => ({
      actual: t.actual + (Number(d?.actualAmount) || 0),
      gst: t.gst + (Number(d?.totalGstAmount) || 0),
      edutech: t.edutech + (Number(d?.edutech) || 0),
      publication: t.publication + (Number(d?.publication) || 0),
    }),
    { actual: 0, gst: 0, edutech: 0, publication: 0 },
  );

/** Chart series for one type, per day or per month */
const buildSeries = (days, type, grouping) => {
  if (grouping === "month") {
    const byMonth = new Map();
    days.forEach((d) => {
      const key = parseDay(d?._id).format("YYYY-MM");
      byMonth.set(key, (byMonth.get(key) || 0) + (Number(d?.[type]) || 0));
    });
    return {
      labels: [...byMonth.keys()].map((k) => moment(k, "YYYY-MM").format("MMM YYYY")),
      values: [...byMonth.values()],
    };
  }
  return {
    labels: days.map((d) => parseDay(d?._id).format("MMM D")),
    values: days.map((d) => Number(d?.[type]) || 0),
  };
};

/** The forecast API sends "NaN" when there's nothing to average */
const forecastValue = (v) => (v === undefined || v === null || v === "NaN" ? 0 : v);

function CxoDashboard() {
  const dispatch = useDispatch();

  const [range, setRange] = useState(defaultRange); // applied
  const [draft, setDraft] = useState(range); // being edited
  const [days, setDays] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);

  const [selectedType, setSelectedType] = useState("edutech");
  const [grouping, setGrouping] = useState("day"); // "day" | "month"
  const [breakdown, setBreakdown] = useState(null); // which card's popup is open

  /* ---------- API ---------- */
  const fetchData = (r) => {
    setLoading(true);
    dispatch(
      getUserTransactionDetails(
        { startDate: r.startDate, endDate: r.endDate },
        (res) => {
          setDays(Array.isArray(res) ? res : []);
          setGrouping("day"); // as before: a new search shows the daily view
          setLoading(false);
        },
        () => {
          setDays([]);
          setLoading(false);
        },
      ),
    );
    dispatch(getTransactionForecast((res) => res?.length > 0 && setForecast(res[0])));
  };

  useEffect(() => {
    fetchData(range);
  }, []);

  const rangeError =
    draft.startDate && draft.endDate && draft.endDate < draft.startDate ? "End date can't be before the start date" : "";

  const applyRange = (e) => {
    e?.preventDefault();
    if (!draft.startDate || !draft.endDate || rangeError) return;
    setRange(draft);
    fetchData(draft);
  };

  /* ---------- derived ---------- */
  const totals = useMemo(() => computeTotals(days), [days]);
  const totalRevenue = totals.edutech + totals.publication;
  const selectedForecast = forecast?.[selectedType] || EMPTY_FORECAST;
  const series = useMemo(() => buildSeries(days, selectedType, grouping), [days, selectedType, grouping]);

  const chartData = {
    labels: series.labels,
    datasets: [
      {
        label: `${TYPES[selectedType]} Revenue`,
        data: series.values,
        borderColor: "rgb(53, 162, 235)",
        backgroundColor: "rgba(53, 162, 235, 0.5)",
        tension: 0.4,
      },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: `${TYPES[selectedType]} Revenue · ${grouping === "month" ? "Monthly" : "Daily"}` },
      tooltip: { callbacks: { label: (ctx) => ` ${formatINR(ctx.parsed.y)}` } },
    },
  };

  // popup rows: same lines as the old GST "View" dropdowns
  const breakdownRows = {
    edutech: [
      { label: "Actual Price", value: formatINR(totals.actual) },
      { label: "GST", value: formatINR(totals.gst) },
      { label: "Total Price", value: formatINR(totals.edutech) },
    ],
    publication: [
      { label: "Publication", value: formatINR(totals.publication) },
      { label: "Total Price", value: formatINR(totals.publication) },
    ],
    total: [
      { label: "Actual Price", value: formatINR(totals.actual) },
      { label: "GST", value: formatINR(totals.gst) },
      { label: "Publication", value: formatINR(totals.publication) },
      { label: "Total Price", value: formatINR(totalRevenue) },
    ],
  };
  const breakdownTitle = { edutech: "Edutech Razorpay", publication: "Publication Razorpay", total: "Total Revenue" };

  const viewButton = (key) => (
    <CustomIconButton
      icon={<Eye size={15} />}
      size="sm"
      title="View GST breakdown"
      onClick={(e) => {
        e?.stopPropagation?.();
        setBreakdown(key);
      }}
    />
  );

  return (
    <CustomPageLayout title="REVENUE ANALYSIS">
      <div className="d-flex flex-column gap-3">
        {/* ---------- date range ---------- */}
        <form
          className="bg-white border rounded-3 shadow-sm p-3 d-flex flex-wrap align-items-end gap-3"
          onSubmit={applyRange}
          noValidate
        >
          <CustomDatePicker
            colClass="col-md-3 col-12"
            label="Start Date"
            isRequired
            value={draft.startDate}
            maxDate={draft.endDate || moment().format("YYYY-MM-DD")}
            onChange={(v) => setDraft((d) => ({ ...d, startDate: v }))}
            clearable={false}
            noMargin
          />
          <CustomDatePicker
            colClass="col-md-3 col-12"
            label="End Date"
            isRequired
            value={draft.endDate}
            minDate={draft.startDate}
            maxDate={moment().format("YYYY-MM-DD")}
            onChange={(v) => setDraft((d) => ({ ...d, endDate: v }))}
            error={rangeError}
            clearable={false}
            noMargin
          />
          <CustomButton
            type="submit"
            icon={<Search size={15} />}
            disabled={!draft.startDate || !draft.endDate || Boolean(rangeError)}
            loading={loading}
          >
            Search
          </CustomButton>
          {/* <span className="text-muted small d-inline-flex align-items-center gap-1 ms-auto">
            <CalendarRange size={14} />
            {formatDateRange(range.startDate, range.endDate)}
          </span> */}
        </form>

        {/* ---------- revenue (click a card to chart it) ---------- */}
        <div className="row g-3">
          <CustomStatCard
            colClass="col-lg-4 col-sm-6 col-12"
            title="Edutech Razorpay"
            value={formatINR(totals.actual)}
            subtitle="Actual amount, excluding GST · click to chart"
            icon={<GraduationCap size={18} />}
            tone="primary"
            active={selectedType === "edutech"}
            loading={loading}
            onClick={() => setSelectedType("edutech")}
          />
          <CustomStatCard
            colClass="col-lg-4 col-sm-6 col-12"
            title="Publication Razorpay"
            value={formatINR(totals.publication)}
            subtitle="Click to chart"
            icon={<BookOpen size={18} />}
            tone="info"
            active={selectedType === "publication"}
            loading={loading}
            onClick={() => setSelectedType("publication")}
          />
          <CustomStatCard
            colClass="col-lg-4 col-12"
            title="Total Revenue"
            value={formatINR(totalRevenue)}
            subtitle="Edutech + Publication"
            icon={<IndianRupee size={18} />}
            tone="success"
            loading={loading}
            action={viewButton("total")}
          />
        </div>

        {/* GST breakdown for each card */}
        <div className="d-flex flex-wrap gap-2">
          <CustomButton variant="outline" size="sm" icon={<Eye size={14} />} onClick={() => setBreakdown("edutech")}>
            Edutech breakdown
          </CustomButton>
          <CustomButton variant="outline" size="sm" icon={<Eye size={14} />} onClick={() => setBreakdown("publication")}>
            Publication breakdown
          </CustomButton>
        </div>

        {/* ---------- forecast for the selected type ---------- */}
        <div className="row g-3">
          <CustomStatCard
            colClass="col-sm-6 col-12"
            title={`Average Sale · ${TYPES[selectedType]}`}
            value={`₹ ${forecastValue(selectedForecast.avgSales)}`}
            icon={<Wallet size={18} />}
            tone="neutral"
            loading={loading}
          />
          <CustomStatCard
            colClass="col-sm-6 col-12"
            title={`Sale Forecast · ${TYPES[selectedType]}`}
            value={`₹ ${forecastValue(selectedForecast.saleForecast)}`}
            icon={<TrendingUp size={18} />}
            tone="warning"
            loading={loading}
          />
        </div>

        {/* ---------- chart (same line chart; Daily / Monthly) ---------- */}
        <section className="bg-white border rounded-3 shadow-sm p-3">
          <div className="d-flex justify-content-center gap-2 mb-3" role="group" aria-label="Chart grouping">
            <CustomButton
              size="sm"
              variant={grouping === "day" ? "primary" : "outline"}
              onClick={() => setGrouping("day")}
              aria-pressed={grouping === "day"}
            >
              Daily
            </CustomButton>
            <CustomButton
              size="sm"
              variant={grouping === "month" ? "primary" : "outline"}
              onClick={() => setGrouping("month")}
              aria-pressed={grouping === "month"}
            >
              Monthly
            </CustomButton>
          </div>
          {days.length > 0 ? (
            <div style={{ height: 360 }}>
              <Line options={chartOptions} data={chartData} />
            </div>
          ) : (
            <div className="text-center text-muted py-5">
              {loading ? "Loading chart…" : "No transactions for this period."}
            </div>
          )}
        </section>
      </div>

      <TotalsModal
        open={Boolean(breakdown)}
        onClose={() => setBreakdown(null)}
        subtitle={breakdown ? `${breakdownTitle[breakdown]} · ${formatDateRange(range.startDate, range.endDate)}` : ""}
        rows={breakdown ? breakdownRows[breakdown] : []}
      />
    </CustomPageLayout>
  );
}

export default CxoDashboard;
